import React, { useState, useEffect } from 'react';
import { getDevices, getDrivers } from '../traccar';
import { getLoads } from '../loadStore';
import { getTrailers } from '../trailerStore';
import {
  getTrips, putTrip, tripWithStatus, addLoadToTrip, handoffTrip, removeLoadFromTrip,
  TRIP_STATUSES, TRIP_STATUS_COLORS,
} from '../tripStore';
import { playOpen, playClose } from '../clickSound';

const lbl = { fontSize: '10px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '3px' };
const inp = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const nextOf = (s) => { const i = TRIP_STATUSES.indexOf(s); return TRIP_STATUSES[Math.min(i + 1, TRIP_STATUSES.length - 1)]; };
const prevOf = (s) => { const i = TRIP_STATUSES.indexOf(s); return TRIP_STATUSES[Math.max(i - 1, 0)]; };

export default function TripsPage() {
  const [trips, setTrips] = useState([]);
  const [loads, setLoads] = useState([]);
  const [devices, setDevices] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [addTo, setAddTo] = useState(null); // trip we're adding a load to
  const [transfer, setTransfer] = useState(null); // { trip } being handed off
  const [tAssign, setTAssign] = useState({ vehicleId: '', driverId: '', coDriverId: '', trailerId: '', reason: 're-power', location: '' });

  const refresh = async () => { setTrips(await getTrips()); setLoads(await getLoads()); };
  useEffect(() => {
    refresh();
    getDevices().then(setDevices).catch(() => {});
    getDrivers().then(setDrivers).catch(() => {});
    getTrailers().then(setTrailers).catch(() => {});
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);

  const vName = (id) => (devices.find((d) => String(d.id) === String(id)) || {}).name || '—';
  const dName = (id) => (drivers.find((d) => String(d.id) === String(id)) || {}).name || '—';
  const trName = (id) => (trailers.find((t) => String(t.id) === String(id)) || {}).unit || '—';
  const loadById = (id) => loads.find((l) => l.id === id);
  const unassigned = loads.filter((l) => !l.tripId && l.status !== 'Complete' && l.status !== 'Invoiced');

  const advance = async (trip, dir) => { await putTrip(tripWithStatus(trip, dir === 'up' ? nextOf(trip.status) : prevOf(trip.status))); refresh(); };

  const doAddLoad = async (loadId) => {
    const load = loadById(loadId); if (!load) return;
    await addLoadToTrip(addTo.id, load); setAddTo(null); playClose(); refresh();
  };

  const openTransfer = (trip) => { playOpen(); setTAssign({ vehicleId: trip.truckId || '', driverId: '', coDriverId: '', trailerId: trip.trailerId || '', reason: 're-power', location: '' }); setTransfer({ trip }); };
  const doTransfer = async () => {
    if (!tAssign.vehicleId || !tAssign.driverId || !tAssign.trailerId) { alert('Pick the truck, driver and trailer that will continue the trip.'); return; }
    const driverIds = [tAssign.driverId, tAssign.coDriverId].filter(Boolean);
    await handoffTrip(transfer.trip.id, { truckId: tAssign.vehicleId, driverIds, trailerId: tAssign.trailerId }, { reason: tAssign.reason, location: tAssign.location });
    setTransfer(null); playClose(); refresh();
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>🧭 Trips</h1>
        <p>The execution plan. A trip carries the driver + equipment; one trip can haul several loads, and a load can move across trips (re-power, breakdown, driver swap) while staying one record.</p>
      </div>

      {trips.length === 0 && <p className="text-muted">No trips yet. Assign a driver, truck and trailer to a load on the Dispatch tab — that creates the trip.</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '14px' }}>
        {trips.map((trip) => {
          const color = TRIP_STATUS_COLORS[trip.status] || '#64748b';
          const tripLoads = (trip.loadIds || []).map(loadById).filter(Boolean);
          return (
            <div key={trip.id} style={{ background: 'white', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 3px rgba(15,23,42,0.1)', borderTop: `3px solid ${color}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 800, fontSize: '16px', color: '#0f172a', flex: 1 }}>Trip {trip.tripNumber}</span>
                <button onClick={() => advance(trip, 'down')} title="Back a status" style={{ background: '#e2e8f0', color: '#334155', fontSize: '11px', fontWeight: 700, padding: '3px 7px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>‹</button>
                <span style={{ fontSize: '11px', fontWeight: 800, color: 'white', background: color, borderRadius: '999px', padding: '3px 10px' }}>{trip.status}</span>
                <button onClick={() => advance(trip, 'up')} title="Advance status" style={{ background: color, color: 'white', fontSize: '11px', fontWeight: 700, padding: '3px 7px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>›</button>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                <div style={{ fontSize: '12px', color: '#334155', flex: 1 }}>
                  🚛 {vName(trip.truckId)} · 🚚 {trName(trip.trailerId)}<br />
                  👤 {(trip.driverIds || []).map(dName).join(' + ') || '—'}{(trip.driverIds || []).length > 1 ? ' (team)' : ''}
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>{trip.origin || '—'} → {trip.destination || '—'}</div>
                </div>
                <button onClick={() => openTransfer(trip)} title="Hand off to a new driver — same trip number" style={{ background: '#fff7ed', color: '#c2410c', fontSize: '11px', fontWeight: 700, padding: '5px 9px', borderRadius: '6px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' }}>⇄ Hand off</button>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <span style={lbl}>Loads on this trip ({tripLoads.length})</span>
                {tripLoads.map((l) => (
                  <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: '8px', padding: '6px 8px', marginBottom: '5px' }}>
                    <span style={{ fontWeight: 800, color: '#0066ff', fontSize: '12px' }}>{l.billNumber}</span>
                    <span style={{ fontSize: '11px', color: '#475569', flex: 1 }}>{(l.shipper && l.shipper.city) || '—'} → {(l.consignee && l.consignee.city) || '—'} · {l.status}</span>
                    <button onClick={async () => { await removeLoadFromTrip(trip.id, l.id); refresh(); }} title="Remove from trip" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '10px', fontWeight: 700, padding: '3px 6px', borderRadius: '5px', border: 'none', cursor: 'pointer' }}>×</button>
                  </div>
                ))}
                <button onClick={() => { playOpen(); setAddTo(trip); }} style={{ background: '#eef2ff', color: '#1d4ed8', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', marginTop: '2px' }}>+ Add load</button>
              </div>

              {(trip.segments || []).length > 0 && (
                <details>
                  <summary style={{ fontSize: '11px', color: '#64748b', cursor: 'pointer', fontWeight: 700 }}>Driver legs / assignment history ({trip.segments.length})</summary>
                  <div style={{ marginTop: '6px' }}>
                    {trip.segments.map((s) => (
                      <div key={s.id} style={{ fontSize: '11px', color: '#475569', padding: '4px 0', borderBottom: '1px solid #f1f5f9' }}>
                        <b>Leg {s.seq}:</b> {(s.driverIds || []).map(dName).join(' + ') || '—'} · {s.from || '—'} → {s.to || '—'}
                        <div style={{ color: '#94a3b8' }}>🚛 {vName(s.truckId)} · 🚚 {trName(s.trailerId)} · {s.reason}{s.miles ? ` · ${s.miles} mi` : ''} · {s.endTime ? 'closed' : 'active'}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>

      {/* add-load modal */}
      {addTo && (
        <div onClick={() => { playClose(); setAddTo(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(440px, 96vw)', padding: '20px', boxSizing: 'border-box', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ margin: '0 0 10px', fontFamily: 'Poppins, sans-serif' }}>Add a load to Trip {addTo.tripNumber}</h3>
            {unassigned.length === 0 && <p className="text-muted" style={{ fontSize: '13px' }}>No unassigned loads available.</p>}
            {unassigned.map((l) => (
              <div key={l.id} onClick={() => doAddLoad(l.id)} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '8px', border: '1px solid #eef2f7', borderRadius: '8px', marginBottom: '6px', cursor: 'pointer' }}>
                <span style={{ fontWeight: 800, color: '#0066ff', fontSize: '13px' }}>{l.billNumber}</span>
                <span style={{ fontSize: '12px', color: '#475569', flex: 1 }}>{(l.shipper && l.shipper.city) || '—'} → {(l.consignee && l.consignee.city) || '—'}</span>
                <span style={{ fontSize: '11px', color: '#1d4ed8', fontWeight: 700 }}>Add ›</span>
              </div>
            ))}
            <div style={{ textAlign: 'right', marginTop: '10px' }}><button onClick={() => { playClose(); setAddTo(null); }} style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>Close</button></div>
          </div>
        </div>
      )}

      {/* transfer / re-power modal */}
      {transfer && (
        <div onClick={() => { playClose(); setTransfer(null); }} style={{ position: 'fixed', inset: 0, background: 'rgba(10,26,47,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', width: 'min(460px, 96vw)', padding: '22px', boxSizing: 'border-box' }}>
            <h3 style={{ margin: '0 0 4px', fontFamily: 'Poppins, sans-serif' }}>⇄ Hand off Trip {transfer.trip.tripNumber}</h3>
            <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px' }}>Same trip number. This closes the current driver's leg here and opens a new leg for the driver taking over — each driver keeps their own miles and work. Any loads on the trip continue.</p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
              <div style={{ flex: 1 }}><span style={lbl}>Reason</span>
                <select style={inp} value={tAssign.reason} onChange={(e) => setTAssign({ ...tAssign, reason: e.target.value })}>
                  {['re-power', 'breakdown', 'driver swap', 'relay', 'cross-dock'].map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}><span style={lbl}>Handoff location</span>
                <input style={inp} value={tAssign.location} onChange={(e) => setTAssign({ ...tAssign, location: e.target.value })} placeholder="e.g. Atlanta, GA" /></div>
            </div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>🚛 New truck</span>
              <select style={inp} value={tAssign.vehicleId} onChange={(e) => setTAssign({ ...tAssign, vehicleId: e.target.value })}>
                <option value="">— choose —</option>{devices.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>🚚 New trailer</span>
              <select style={inp} value={tAssign.trailerId} onChange={(e) => setTAssign({ ...tAssign, trailerId: e.target.value })}>
                <option value="">— choose —</option>{trailers.map((t) => <option key={t.id} value={t.id}>{t.unit}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>👤 New driver</span>
              <select style={inp} value={tAssign.driverId} onChange={(e) => setTAssign({ ...tAssign, driverId: e.target.value })}>
                <option value="">— choose —</option>{drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: '10px' }}><span style={lbl}>👥 Co-driver (optional)</span>
              <select style={inp} value={tAssign.coDriverId} onChange={(e) => setTAssign({ ...tAssign, coDriverId: e.target.value })}>
                <option value="">— none —</option>{drivers.filter((d) => String(d.id) !== String(tAssign.driverId)).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button onClick={() => { playClose(); setTransfer(null); }} style={{ background: '#e2e8f0', color: '#334155', padding: '8px 16px', borderRadius: '6px', fontWeight: 600, border: 'none', cursor: 'pointer' }}>Cancel</button>
              <button onClick={doTransfer} style={{ background: 'linear-gradient(135deg,#f97316,#f4511e)', color: 'white', padding: '8px 16px', borderRadius: '6px', fontWeight: 700, border: 'none', cursor: 'pointer' }}>Hand off (same trip)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
